import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, useTheme, API } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  User, 
  Moon, 
  Sun, 
  SignOut, 
  Gear, 
  Crown, 
  Info, 
  EnvelopeSimple, 
  ShieldCheck, 
  FileText, 
  CreditCard,
  CaretRight
} from '@phosphor-icons/react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cardio-journey-app/artifacts/tyj7q2ct_logo%20365.png";

const Profile = () => {
  const navigate = useNavigate();
  const { user, logout, token } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.get(`${API}/profile`, { withCredentials: true, headers });
        setProfile(response.data);
      } catch (error) {
        console.error('Failed to fetch profile:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [token]);

  const handleLogout = async () => {
    await logout();
    navigate('/welcome');
    toast.success('Logged out successfully');
  };

  if (loading) {
    return (<div className="min-h-screen bg-background flex items-center justify-center"><div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin" /></div>);
  }

  const menuItems = [
    { icon: Info, label: 'About Track365', path: '/about' },
    { icon: EnvelopeSimple, label: 'Contact Us', path: '/contact' },
    { icon: CreditCard, label: 'Subscription & Billing', path: '/subscription' },
    { icon: ShieldCheck, label: 'Privacy Policy', path: '/privacy' },
    { icon: FileText, label: 'Terms of Service', path: '/terms' },
  ];

  return (
    <div className="min-h-screen bg-background relative overflow-hidden" data-testid="profile-screen">
      {/* Background logo */}
      <div 
        className="absolute inset-0 opacity-[0.03] bg-center bg-no-repeat bg-contain pointer-events-none"
        style={{ backgroundImage: `url(${LOGO_URL})` }}
      />

      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border px-6 py-4">
        <div className="max-w-lg mx-auto flex items-center gap-4">
          <button onClick={() => navigate('/dashboard')} className="w-10 h-10 rounded-full bg-card flex items-center justify-center" data-testid="back-btn">
            <ArrowLeft weight="bold" className="w-5 h-5 text-foreground" />
          </button>
          <div className="flex-1"><h1 className="text-xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>Profile</h1></div>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-6 py-6 space-y-6 relative z-10">
        {/* User Info */}
        <div className="premium-card p-6 text-center">
          <div className="w-20 h-20 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-4 border-2 border-[#D4AF37]/30">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-20 h-20 rounded-full" />
            ) : (
              <User weight="duotone" className="w-10 h-10 text-[#D4AF37]" />
            )}
          </div>
          <h2 className="text-xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>{user?.name}</h2>
          <p className="text-muted-foreground text-sm">{user?.email}</p>
          {user?.role === 'admin' && (
            <span className="inline-flex items-center gap-1 mt-2 text-xs bg-[#D4AF37]/20 text-[#D4AF37] px-3 py-1 rounded-full">
              <Crown weight="fill" className="w-3 h-3" />Admin
            </span>
          )}
        </div>

        {/* Theme Toggle */}
        <div className="premium-card p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {theme === 'dark' ? <Moon weight="duotone" className="w-6 h-6 text-[#D4AF37]" /> : <Sun weight="duotone" className="w-6 h-6 text-[#D4AF37]" />}
              <div>
                <Label className="text-foreground font-medium">Dark Mode</Label>
                <p className="text-xs text-muted-foreground">Toggle between light and dark theme</p>
              </div>
            </div>
            <Switch checked={theme === 'dark'} onCheckedChange={toggleTheme} data-testid="theme-toggle" />
          </div>
        </div>

        {/* Profile Details */}
        {profile?.profile && Object.keys(profile.profile).length > 0 && (
          <div className="premium-card p-5">
            <h3 className="text-xs text-[#D4AF37] uppercase tracking-wider font-semibold mb-4">Your Settings</h3>
            <div className="space-y-3">
              {profile.profile.fitness_level && (
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground text-sm">Fitness Level</span>
                  <span className="text-foreground font-medium capitalize">{profile.profile.fitness_level}</span>
                </div>
              )}
              {profile.profile.days_per_week && (
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground text-sm">Days Per Week</span>
                  <span className="text-foreground font-medium">{profile.profile.days_per_week}</span>
                </div>
              )}
              {profile.profile.target_session_minutes && (
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground text-sm">Target Session</span>
                  <span className="text-foreground font-medium">{profile.profile.target_session_minutes} min</span>
                </div>
              )}
              {profile.profile.current_weight && (
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground text-sm">Current Weight</span>
                  <span className="text-foreground font-medium">{profile.profile.current_weight} lbs</span>
                </div>
              )}
              {profile.profile.goal_weight && (
                <div className="flex justify-between py-2">
                  <span className="text-muted-foreground text-sm">Goal Weight</span>
                  <span className="text-foreground font-medium">{profile.profile.goal_weight} lbs</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Menu Items */}
        <div className="premium-card overflow-hidden">
          {menuItems.map((item, index) => {
            const Icon = item.icon;
            return (
              <Link 
                key={item.path} 
                to={item.path}
                className={`flex items-center justify-between p-4 hover:bg-card/50 transition-colors ${index < menuItems.length - 1 ? 'border-b border-border' : ''}`}
                data-testid={`menu-${item.path.slice(1)}`}
              >
                <div className="flex items-center gap-3">
                  <Icon weight="duotone" className="w-5 h-5 text-[#D4AF37]" />
                  <span className="text-foreground">{item.label}</span>
                </div>
                <CaretRight weight="bold" className="w-4 h-4 text-muted-foreground" />
              </Link>
            );
          })}
        </div>

        {/* Admin Link */}
        {user?.role === 'admin' && (
          <Button onClick={() => navigate('/admin')} variant="outline" className="w-full border-[#D4AF37]/30 text-foreground rounded-xl py-6 hover:bg-[#D4AF37]/5" data-testid="admin-link">
            <Gear weight="duotone" className="w-5 h-5 mr-2 text-[#D4AF37]" />
            Admin Dashboard
          </Button>
        )}

        {/* Logout */}
        <Button onClick={handleLogout} variant="outline" className="w-full border-red-500/30 text-red-500 rounded-xl py-6 hover:bg-red-500/5" data-testid="logout-btn">
          <SignOut weight="bold" className="w-5 h-5 mr-2" />
          Log Out
        </Button>
      </main>
    </div>
  );
};

export default Profile;
